import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  QrCode as QrCodeIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  getInventoryItemsPaginated,
  getSoldItems,
  updateItemStatus,
  INVENTORY_ITEM_STATUS,
} from '../services/inventoryItemService';

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 1, label: 'Available' },
  { value: 2, label: 'Sold' },
  { value: 3, label: 'Reserved' },
  { value: 4, label: 'Returned' },
  { value: 5, label: 'Damaged' },
  { value: 6, label: 'In Transit' },
];

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ statusValue, statusLabel }) => {
  const info = INVENTORY_ITEM_STATUS[statusValue] || { label: statusLabel || 'Unknown', color: 'default' };
  return <Chip size="small" label={info.label} color={info.color} />;
};

// ─── Item Details Dialog ──────────────────────────────────────────────────────
const ItemDetailsDialog = ({ item, open, onClose }) => {
  if (!item) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>Item Details</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {[
            ['Product', item.productName],
            ['Variant', item.variantName],
            ['IMEI', item.imei],
            ['Serial Number', item.serialNumber],
            ['Status', null],
            ['Store', item.storeName],
            ['Supplier', item.supplierName],
            ['Purchase Price', item.purchasePrice ? `€${Number(item.purchasePrice).toFixed(2)}` : '-'],
            ['Selling Price', item.sellingPrice ? `€${Number(item.sellingPrice).toFixed(2)}` : '-'],
            ['Purchase Invoice', item.purchaseInvoiceNumber],
            ['Purchase Date', item.formattedPurchaseDate || item.purchaseDate?.split('T')[0]],
            ['Warranty Expiry', item.warrantyExpiryDate ? item.warrantyExpiryDate.split('T')[0] : '-'],
            ['Notes', item.notes],
          ].map(([label, value]) => (
            <Grid item xs={12} sm={6} key={label}>
              <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
              {label === 'Status' ? (
                <StatusBadge statusValue={item.statusValue} statusLabel={item.status} />
              ) : (
                <Typography variant="body2" fontWeight={500}>{value || '-'}</Typography>
              )}
            </Grid>
          ))}
          {item.customerName && (
            <>
              <Grid item xs={12}><Divider><Typography variant="caption">Sale Info</Typography></Divider></Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Customer</Typography>
                <Typography variant="body2" fontWeight={500}>{item.customerName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Invoice</Typography>
                <Typography variant="body2" fontWeight={500}>{item.saleInvoiceNumber || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">Sale Date</Typography>
                <Typography variant="body2" fontWeight={500}>{item.formattedSaleDate || '-'}</Typography>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Status Update Dialog ─────────────────────────────────────────────────────
const StatusUpdateDialog = ({ item, open, onClose, onUpdated }) => {
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) { setNewStatus(''); setNotes(''); }
  }, [open]);

  const handleSave = async () => {
    if (!newStatus) return;
    setLoading(true);
    try {
      await updateItemStatus(item.id, parseInt(newStatus, 10), notes);
      toast.success('Status updated');
      onUpdated();
      onClose();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  if (!item) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Update Status</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            IMEI: <strong>{item.imei || item.serialNumber}</strong>
          </Typography>
          <TextField
            select
            fullWidth
            label="New Status"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
          >
            {STATUS_OPTIONS.filter((s) => s.value !== '').map((s) => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || !newStatus}>
          {loading ? <CircularProgress size={16} /> : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Dashboard Stats ──────────────────────────────────────────────────────────
const StatCard = ({ label, value, color }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, borderTop: 4, borderTopColor: `${color}.main` }}>
    <CardContent sx={{ textAlign: 'center', py: 2 }}>
      <Typography variant="h5" fontWeight={700} color={`${color}.main`}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </CardContent>
  </Card>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const InventoryItemsPage = () => {
  const { t } = useTranslation();

  // Table state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0); // 0-based for MUI
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Dialogs
  const [detailItem, setDetailItem] = useState(null);
  const [statusItem, setStatusItem] = useState(null);

  // Stats
  const [stats, setStats] = useState({ available: 0, sold: 0, damaged: 0, total: 0 });

  const searchTimerRef = useRef(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getInventoryItemsPaginated({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        searchTerm: searchQuery || null,
        status: statusFilter !== '' ? statusFilter : null,
      });
      const payload = res?.payload;
      if (payload) {
        const itemList = payload.items || payload || [];
        setItems(itemList);
        setTotalItems(payload.totalCount || payload.totalItems || itemList.length);
        // Build stats from first load (no filter)
        if (!statusFilter && !searchQuery) {
          const byStatus = {};
          itemList.forEach((i) => { byStatus[i.statusValue] = (byStatus[i.statusValue] || 0) + 1; });
          setStats({
            total: payload.totalCount || itemList.length,
            available: byStatus[1] || 0,
            sold: byStatus[2] || 0,
            damaged: byStatus[5] || 0,
          });
        }
      } else {
        setItems([]);
      }
    } catch {
      toast.error('Failed to load inventory items');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, statusFilter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSearchChange = (e) => {
    clearTimeout(searchTimerRef.current);
    const val = e.target.value;
    searchTimerRef.current = setTimeout(() => {
      setSearchQuery(val);
      setPage(0);
    }, 400);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Inventory Item Tracking</Typography>
          <Typography variant="body2" color="text.secondary">
            Track individual items by IMEI / Serial Number across the full lifecycle.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchItems}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      {/* Stats row */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}><StatCard label="Total Items" value={stats.total} color="primary" /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Available" value={stats.available} color="success" /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Sold" value={stats.sold} color="info" /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Damaged" value={stats.damaged} color="error" /></Grid>
      </Grid>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="Search IMEI, serial, product, variant..."
              defaultValue={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              label="Status"
              value={statusFilter}
              onChange={handleStatusChange}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Product / Variant</strong></TableCell>
                <TableCell><strong>IMEI</strong></TableCell>
                <TableCell><strong>Serial #</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Store</strong></TableCell>
                <TableCell><strong>Purchase Date</strong></TableCell>
                <TableCell><strong>Cost</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <QrCodeIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No items found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} noWrap>{item.productName}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.variantName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">{item.imei || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">{item.serialNumber || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusBadge statusValue={item.statusValue} statusLabel={item.status} />
                    </TableCell>
                    <TableCell>{item.storeName || '-'}</TableCell>
                    <TableCell>{item.formattedPurchaseDate || item.purchaseDate?.split('T')[0] || '-'}</TableCell>
                    <TableCell>
                      {item.purchasePrice ? `€${Number(item.purchasePrice).toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => setDetailItem(item)}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* Only allow status update for non-sold items */}
                      {item.statusValue !== 2 && (
                        <Tooltip title="Update Status">
                          <IconButton size="small" onClick={() => setStatusItem(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>

      {/* Dialogs */}
      <ItemDetailsDialog item={detailItem} open={Boolean(detailItem)} onClose={() => setDetailItem(null)} />
      <StatusUpdateDialog
        item={statusItem}
        open={Boolean(statusItem)}
        onClose={() => setStatusItem(null)}
        onUpdated={fetchItems}
      />
    </Box>
  );
};

export default InventoryItemsPage;
